import { useCallback, useLayoutEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { readMachineStatus } from 'tf-pulse-shared';
import ChevronLeftIcon from '../assets/icons/chevron-left.svg?react';
import { IconButton } from '../components/IconButton';
import { useHeader } from '../contexts/HeaderContext';
import { useGetMachineQuery } from '../gql/generated';
import { useLayoutTier } from '../hooks/useLayoutTier';
import { MachineDetailCompact } from './machines/MachineDetailCompact';
import { MachineHeartbeatNote } from './machines/MachineHeartbeatNote';
import { MachineSignalCategories } from './machines/MachineSignalCategories';
import { hasMachineSignals } from '../utils/machineDisplay';
import { MachineStatusBadge } from './machines/MachineStatusBadge';
import { getMachineStatusTone } from './machines/machineStatusTone';
import { readMachineDetailPreview } from './machines/machineDetailPreview';
import {
  navigateWithTransition,
  usePageTransitionReady,
} from '../utils/viewTransition';

interface PageHeadingProps {
  onBack: () => void;
}

/** The heading a desktop needs. Elsewhere the app bar carries it instead. */
function PageHeading({ onBack }: PageHeadingProps) {
  return (
    <div className='flex items-center gap-4'>
      <IconButton
        icon={<ChevronLeftIcon />}
        onClick={onBack}
        aria-label='Back'
        iconSize='lg'
      />
      <h1 className='size-h1 text-primary font-bold'>Machine Details</h1>
    </div>
  );
}

export function MachineDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isTabletDown } = useLayoutTier();
  const { setAppBarTone, setBackAction, setMobileActionButton, setPageTitle } =
    useHeader();
  const goBack = useCallback(
    () => navigateWithTransition('pop', () => navigate(-1)),
    [navigate]
  );
  usePageTransitionReady();

  // The app bar carries the title and the way back on the compact tiers.
  useLayoutEffect(() => {
    if (!isTabletDown) return;

    setPageTitle('Machine detail');
    setBackAction(goBack);

    return () => {
      setPageTitle(null);
      setBackAction(null);
    };
  }, [goBack, isTabletDown, setBackAction, setPageTitle]);

  const { data, loading, error } = useGetMachineQuery({
    variables: { id: id || '' },
    skip: !id,
  });

  const machine = data?.machine;
  const hasMachine = Boolean(machine);
  const { status: machineStatus } = readMachineStatus(machine?.state);
  const machineName = machine ? machine.displayName || machine.machineId : null;

  // The list passes what it knows, so the app bar is right while this loads.
  const preview = readMachineDetailPreview(location.state);
  const shownName = machineName ?? preview?.name ?? null;
  const shownStatus = hasMachine ? machineStatus : (preview?.status ?? null);

  // The app bar names the machine and takes its status colour.
  useLayoutEffect(() => {
    if (!isTabletDown || !shownName || !shownStatus) return;

    setPageTitle(shownName);
    setAppBarTone(getMachineStatusTone(shownStatus));

    return () => {
      setAppBarTone('focus');
    };
  }, [isTabletDown, shownName, shownStatus, setPageTitle, setAppBarTone]);

  useLayoutEffect(() => {
    if (!isTabletDown || !shownStatus) return;

    setMobileActionButton(<MachineStatusBadge status={shownStatus} />);

    return () => setMobileActionButton(null);
  }, [isTabletDown, shownStatus, setMobileActionButton]);

  if (loading) {
    return (
      <div className='flex flex-1 items-center justify-center p-8'>
        <div className='text-secondary text-center'>
          <div
            className='mb-4 inline-block h-12 w-12 animate-spin rounded-full
              border-4 border-gray-300 border-t-blue-600'
          ></div>
          <p>Loading machine details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex flex-col gap-4 p-8'>
        {!isTabletDown && <PageHeading onBack={goBack} />}
        <div
          className='text-error bg-red-050 rounded-lg border border-red-300 p-4'
        >
          <p className='font-medium'>Error loading machine</p>
          <p className='size-p2 mt-1'>{error.message}</p>
        </div>
      </div>
    );
  }

  if (!machine) {
    return (
      <div className='flex flex-col gap-4 p-8'>
        {!isTabletDown && <PageHeading onBack={goBack} />}
        <div
          className='text-warning rounded-lg border border-yellow-300
            bg-yellow-50 p-4'
        >
          <p className='font-medium'>Machine not found</p>
          <p className='size-p2 mt-1'>
            The machine with ID &quot;{id}&quot; could not be found.
          </p>
        </div>
      </div>
    );
  }

  if (isTabletDown) {
    return <MachineDetailCompact machine={machine} />;
  }

  return (
    <div className='flex flex-col gap-6'>
      {/* Header with back button */}
      <div className='flex items-center gap-4 bg-white p-4 md:px-8 md:py-6'>
        <IconButton
          icon={<ChevronLeftIcon />}
          onClick={goBack}
          aria-label='Back'
          iconSize='lg'
        />
        <div className='flex items-center gap-3'>
          <h1 className='size-h1 text-primary font-bold'>
            {machine.displayName || machine.machineId}
          </h1>
          <MachineStatusBadge status={machineStatus} />
          <MachineHeartbeatNote state={machine.state} />
        </div>
      </div>

      {/* Machine Information Sections */}
      <div className='flex flex-col gap-6 p-4 md:px-8'>
        {/* Basic Information */}
        <section className='bg-surface rounded-lg border border-gray-200 p-6'>
          <h2 className='size-h3 text-primary mb-4 font-semibold'>
            Basic Information
          </h2>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
            <InfoItem label='Machine ID' value={machine.machineId} />
            <InfoItem
              label='Display Name'
              value={machine.displayName || machine.machineId}
            />
            <InfoItem label='Serial Number' value={machine.serial} />
            <InfoItem label='Part Number' value={machine.partNumber} />
            <InfoItem label='SOID' value={machine.soid} />
            <InfoItem label='Family' value={machine.family} />
          </div>
        </section>

        {/* Customer Information - always rendered: missing data shows as
            'Not available' rather than a silently dropped section */}
        <section className='bg-surface rounded-lg border border-gray-200 p-6'>
          <h2 className='size-h3 text-primary mb-4 font-semibold'>
            Customer Information
          </h2>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <InfoItem label='Customer ID' value={machine.customer?.id} />
            <InfoItem label='IFS ID' value={machine.customer?.ifsId} />
            <InfoItem label='Name' value={machine.customer?.name} />
            <InfoItem
              label='Display Name'
              value={machine.customer?.displayName}
            />
          </div>
        </section>

        {/* Plant Information - always rendered, like Customer Information */}
        <section className='bg-surface rounded-lg border border-gray-200 p-6'>
          <h2 className='size-h3 text-primary mb-4 font-semibold'>
            Plant Information
          </h2>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
            <InfoItem label='Plant ID' value={machine.plant?.id} />
            <InfoItem label='Plant Name' value={machine.plant?.displayName} />
            <InfoItem label='Country' value={machine.address?.country} />
            <InfoItem
              label='Country Code'
              value={machine.address?.countryCode}
            />
          </div>
        </section>

        {/* Edge Device Information - always rendered: a machine without an
            edge device should say so rather than silently drop the section */}
        <section className='bg-surface rounded-lg border border-gray-200 p-6'>
          <h2 className='size-h3 text-primary mb-4 font-semibold'>
            Edge Device Information
          </h2>
          {machine.edgeDevice ? (
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <InfoItem
                label='Edge Device ID (IoT)'
                value={machine.edgeDevice.deviceId}
              />
              <InfoItem
                label='Edge Device Name'
                value={machine.edgeDevice.displayName}
              />
              <InfoItem
                label='Edge Device DB ID'
                value={machine.edgeDevice.id}
              />
            </div>
          ) : (
            <p className='size-p1 text-tertiary italic'>
              No edge device has been provisioned for this machine.
            </p>
          )}
        </section>

        {/* Technical IDs */}
        <section className='bg-surface rounded-lg border border-gray-200 p-6'>
          <h2 className='size-h3 text-primary mb-4 font-semibold'>
            Technical Information
          </h2>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <InfoItem label='Internal ID' value={machine.id} />
            <InfoItem label='Plant ID Reference' value={machine.plant?.id} />
          </div>
        </section>

        {/* Available Metrics */}
        {hasMachineSignals(machine.availableSignals) && (
          <section className='bg-surface rounded-lg border border-gray-200 p-6'>
            <h2 className='size-h3 text-primary mb-4 font-semibold'>
              Available Metrics
            </h2>
            <MachineSignalCategories
              availableSignals={machine.availableSignals}
              layout='grid'
            />
          </section>
        )}
      </div>
    </div>
  );
}

// Helper component for displaying information items
interface InfoItemProps {
  label: string;
  value?: string | null;
}

function InfoItem({ label, value }: InfoItemProps) {
  return (
    <div className='flex flex-col gap-1'>
      <span className='size-p2 text-secondary font-medium'>{label}</span>
      <span className='size-p1 text-primary'>
        {value || <span className='text-tertiary'>-</span>}
      </span>
    </div>
  );
}
